// src/data/services/servicesRegistry.ts
//
// ─── HOW TO ADD A NEW SERVICE ─────────────────────────────────────────────
//
// 1. Create the JSON file: src/data/services/your-slug.json
//    (Follow the same shape as any existing service JSON)
//
// 2. Import it here:
//    import yourService from "./your-slug.json";
//
// 3. Add it to SERVICES_REGISTRY in the correct order:
//    { ...serviceMeta(yourService) },
//
// Services.tsx, SingleService.tsx, and Home.tsx all update automatically.
// ─────────────────────────────────────────────────────────────────────────────

import webDevelopment    from "./web-development.json";
import mobileApp         from "./mobile-app-development.json";
import techProducts      from "./technology-products.json";
import techConsulting    from "./tech-consulting.json";

// ─── Shared types ─────────────────────────────────────────────────────────

export interface TechStackItem {
  name: string;
  description: string;
}

export interface ProcessStep {
  title: string;
  body: string;
}

export interface CaseStudy {
  title: string;
  excerpt: string;
  href: string;
  image: string;
}

export interface RelatedService {
  title: string;
  href: string;
}

/** Full service data — used by SingleService detail page */
export interface ServiceData {
  slug: string;
  number: string;
  tag: string;
  title: string;
  tagline: string;
  heroImage: string;
  image: string;
  description: string;
  overview: string[];
  deliverables: string[];
  techStack: TechStackItem[];
  techStackTags: string[];
  process: ProcessStep[];
  caseStudy?: CaseStudy;
  relatedServices: RelatedService[];
}

/** Lightweight card — used by Services listing & Home services strip */
export interface ServiceCard {
  slug: string;
  number: string;
  title: string;
  tagline: string;
  description: string;
  deliverables: string[];
  techStack: string[];
  image: string;
  href: string;
}

// ─── Helper ───────────────────────────────────────────────────────────────

function serviceMeta(json: ServiceData): ServiceCard {
  return {
    slug:         json.slug,
    number:       json.number,
    title:        json.title,
    tagline:      json.tagline,
    description:  json.description,
    deliverables: json.deliverables,
    techStack:    json.techStackTags,
    image:        json.image,
    href:         `/services/${json.slug}`,
  };
}

// ─── Registry ─────────────────────────────────────────────────────────────
// Order determines the numbered sequence on the Services page.

export const SERVICES_REGISTRY: ServiceCard[] = [
  serviceMeta(webDevelopment   as unknown as ServiceData),
  serviceMeta(mobileApp        as unknown as ServiceData),
  serviceMeta(techProducts     as unknown as ServiceData),
  serviceMeta(techConsulting   as unknown as ServiceData),
];

// ─── Full data map — used by SingleService ────────────────────────────────

export const SERVICE_DATA_MAP: Record<string, ServiceData> = {
  "web-development":       webDevelopment   as unknown as ServiceData,
  "mobile-app-development": mobileApp       as unknown as ServiceData,
  "technology-products":   techProducts     as unknown as ServiceData,
  "tech-consulting":       techConsulting   as unknown as ServiceData,
};

// ─── Home page services strip ─────────────────────────────────────────────
// Matches the SERVICES array shape that Home.tsx ServicesSection expects.

export const HOME_SERVICES = SERVICES_REGISTRY.map((s, i) => ({
  number: s.number,
  title:  s.title,
  body:   s.description,
  href:   s.href,
}));